/** Server-evaluated read metadata; never a token or authority to send or close. */
export interface CurrentReconciliation {
  status: 'NOT_CHECKED' | 'PENDING' | 'DIFFERS' | 'INVALID' | 'EXPIRED' | 'MATCHED' | 'UNAVAILABLE';
  checkedAt: string | null;
  expiresAt: string | null;
}

export function currentReconciliationStatus(
  check: CurrentReconciliation | undefined,
  now = Date.now(),
): CurrentReconciliation['status'] {
  if (!check) return 'UNAVAILABLE';
  if (check.status !== 'MATCHED' && check.status !== 'PENDING') return check.status;
  const expiry = Date.parse(check.expiresAt ?? '');
  if (!Number.isFinite(expiry)) return 'UNAVAILABLE';
  if (expiry <= now) return 'EXPIRED';
  if (check.status === 'MATCHED') {
    const checked = Date.parse(check.checkedAt ?? '');
    if (!Number.isFinite(checked) || checked > now || checked >= expiry) return 'UNAVAILABLE';
  }
  return check.status;
}

const COPY: Record<CurrentReconciliation['status'], { label: string; nextStep: string }> = {
  NOT_CHECKED: { label: 'Not checked yet', nextStep: 'Check Xero before closing this pay run.' },
  PENDING: {
    label: 'Check in progress',
    nextStep: 'The latest Xero check has not finished. Refresh to see its progress.',
  },
  DIFFERS: {
    label: 'Different — review required',
    nextStep: 'Review the differences listed on this pay run before closing.',
  },
  INVALID: {
    label: 'Pay run changed — check again',
    nextStep: 'The pay run changed after the last check. Check Xero again before closing.',
  },
  EXPIRED: {
    label: 'Check expired',
    nextStep:
      'Check Xero again before closing. The latest check must finish and match within 15 minutes.',
  },
  MATCHED: {
    label: 'Matched',
    nextStep: 'Record independent bank payment evidence before closing this pay run.',
  },
  UNAVAILABLE: {
    label: 'Current check unavailable',
    nextStep:
      'Refresh to load the current Xero check. An unavailable result does not confirm a match.',
  },
};

export function currentReconciliationCopy(
  check: CurrentReconciliation | undefined,
  now = Date.now(),
) {
  return COPY[currentReconciliationStatus(check, now)];
}
